import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

import { parseNumber } from '../format';
import styles from './primitives.module.css';

// UI-примитивы калькуляторов — перенос компонентов прототипа
// (CalcCard / NumField / SelectField / ResultBox / Note / Pill / BlockHeader).
// Инлайн-стили заменены CSS-модулем (решение V1.2-b), разметка та же плюс
// aria-атрибуты раскрытия — их в прототипе не было, а без них аккордеон
// недоступен с клавиатуры и не проверяем в тестах.

/** Числовое поле: строковое значение из input + разобранное число. */
export interface NumberField {
  value: string;
  set: (value: string) => void;
  num: number;
}

/** Состояние одного числового поля калькулятора. */
export function useNumberField(initial: string): NumberField {
  const [value, set] = useState(initial);
  return { value, set, num: parseNumber(value) };
}

export function BlockHeader({
  label,
  count,
  open,
  onToggle,
}: {
  label: string;
  count: number;
  open: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      className={`${styles.blockHeader} ${open ? styles.blockHeaderOpen : ''}`}
      aria-expanded={open}
      onClick={onToggle}
    >
      <span className={styles.blockLabel}>{label}</span>
      <span className={styles.blockMeta}>
        <span className={styles.blockCount}>{count}</span>
        <ChevronDown
          size={14}
          aria-hidden="true"
          className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`}
        />
      </span>
    </button>
  );
}

export function CalcCard({
  label,
  open,
  onToggle,
  children,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className={styles.card}>
      <button type="button" className={styles.cardToggle} aria-expanded={open} onClick={onToggle}>
        <span className={styles.cardLabel}>{label}</span>
        <ChevronDown
          size={15}
          aria-hidden="true"
          className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`}
        />
      </button>
      {open && <div className={styles.cardBody}>{children}</div>}
    </div>
  );
}

export function NumField({
  label,
  field,
  step = '0.1',
}: {
  label: string;
  field: NumberField;
  step?: string;
}): JSX.Element {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <input
        className={styles.input}
        type="number"
        step={step}
        value={field.value}
        onChange={(event) => field.set(event.target.value)}
      />
    </label>
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

export function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
}): JSX.Element {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <select
        className={styles.select}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function ResultBox({ children }: { children: ReactNode }): JSX.Element {
  // div, а не <output>: внутри бывают строки-блоки, а output — phrasing content.
  return (
    <div className={styles.result} aria-live="polite">
      {children}
    </div>
  );
}

export function Note({ children }: { children: ReactNode }): JSX.Element {
  return <p className={styles.note}>{children}</p>;
}

export function Row({ children }: { children: ReactNode }): JSX.Element {
  return <div className={styles.row}>{children}</div>;
}

export function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      className={`${styles.pill} ${active ? styles.pillActive : ''}`}
      aria-pressed={active}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function PresetButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <button type="button" className={styles.preset} onClick={onClick}>
      {children}
    </button>
  );
}
